import { useEffect, useMemo, useState } from 'react';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

import * as S from './styled/SwiperFooter.styled';

/**
 * SwiperFooter component renders a footer for a swiper with navigation buttons and pagination bullets.
 *
 * Add these props to your Swiper component to make this footer work:
 *
 * @param {Object} props - The component props.
 * @param {Object} props.swiperRef - The swiper reference.
 *
 */

const SwiperFooter = ({ swiperRef }) => {
	const [currentIndex, setCurrentIndex] = useState(swiperRef?.activeIndex || 0);
	const totalSlides = useMemo(() => {
		if (swiperRef.params.grid?.rows) {
			return Math.ceil(swiperRef.slides.length / (swiperRef.params.slidesPerView * swiperRef.params.grid.rows));
		}
		return swiperRef.slides.length - swiperRef.params.slidesPerView + 1;
	}, [swiperRef, swiperRef.slides, swiperRef.params]);

	const handlePrev = () => {
		swiperRef?.slidePrev();
	};

	const handleNext = () => {
		swiperRef?.slideNext();
	};

	const handleSlideChange = () => {
		setCurrentIndex(swiperRef?.activeIndex);
	};

	useEffect(() => {
		swiperRef?.on('slideChange', handleSlideChange);

		return () => {
			swiperRef?.off('slideChange', handleSlideChange);
			setCurrentIndex(0);
		};
	}, [swiperRef]);

	return (
		totalSlides >= 1 && (
			<S.FooterWrapper className="swiper-footer">
				<S.NavigationButton
					onClick={handlePrev}
					variant="borderless"
					disabled={currentIndex === 0}
					icon={faChevronLeft}
				/>
				<S.BulletContainer>
					{Array.from({ length: totalSlides }).map((_, index) => (
						<S.Bullet key={index} className={index === currentIndex ? 'active' : ''} />
					))}
				</S.BulletContainer>
				<S.NavigationButton
					onClick={handleNext}
					variant="borderless"
					disabled={currentIndex === totalSlides - 1}
					icon={faChevronRight}
				/>
			</S.FooterWrapper>
		)
	);
};

export default SwiperFooter;
